/**
 * @module components/DonorFields
 * @description Donor information fields with correct `autocomplete` tokens
 * (browser/wallet autofill = less typing). Shared by the single-page checkout
 * (Tier 1) and the guided checkout (Tier 2), which shows a subset per step.
 */
import type { JSX } from "react";
import type { DonorInfo } from "../domain/types";
import type { DonorErrors } from "../domain/donor";

/** Which field groups to render. */
export type DonorFieldGroup = "name" | "contact" | "address" | "privacy";

/** Props for <DonorFields>. */
interface DonorFieldsProps {
    donor: DonorInfo;
    errors: DonorErrors;
    onChange: (donor: DonorInfo) => void;
    groups?: DonorFieldGroup[];
}

/** One labeled text input bound to a DonorInfo key. */
function TextField({ name, label, autoComplete, type = "text", donor, errors, onChange, optional = false }: {
    name: "firstName" | "lastName" | "email" | "phone" | "country" | "postalCode";
    label: string;
    autoComplete: string;
    type?: string;
    donor: DonorInfo;
    errors: DonorErrors;
    onChange: (donor: DonorInfo) => void;
    optional?: boolean;
}): JSX.Element {
    const error: string | undefined = errors[name];
    return (
        <label className="field">
            <span className="field__label">{label}{optional && <span className="field__optional"> (optional)</span>}</span>
            <input className="input" type={type} name={name} autoComplete={autoComplete} value={donor[name]}
                aria-invalid={error !== undefined} aria-describedby={error ? `${name}-error` : undefined}
                onChange={(e) => onChange({ ...donor, [name]: e.target.value })} />
            {error && <span id={`${name}-error`} className="field__error">{error}</span>}
        </label>
    );
}

/** Donor fields, grouped. */
export function DonorFields({ donor, errors, onChange, groups = ["name", "contact", "address", "privacy"] }: DonorFieldsProps): JSX.Element {
    const shared = { donor, errors, onChange };
    return (
        <div className="donor-fields">
            {groups.includes("name") && (
                <div className="donor-fields__row">
                    <TextField name="firstName" label="First name" autoComplete="given-name" {...shared} />
                    <TextField name="lastName" label="Last name" autoComplete="family-name" {...shared} />
                </div>
            )}
            {groups.includes("contact") && (
                <div className="donor-fields__row">
                    <TextField name="email" label="Email (for your receipt)" autoComplete="email" type="email" {...shared} />
                    <TextField name="phone" label="Phone" autoComplete="tel" type="tel" optional {...shared} />
                </div>
            )}
            {groups.includes("address") && (
                <div className="donor-fields__row">
                    <TextField name="country" label="Country" autoComplete="country-name" {...shared} />
                    <TextField name="postalCode" label="ZIP / postal code" autoComplete="postal-code" {...shared} />
                </div>
            )}
            {groups.includes("privacy") && (
                <label className="donor-fields__check">
                    <input type="checkbox" checked={donor.anonymous} onChange={(e) => onChange({ ...donor, anonymous: e.target.checked })} />
                    <span>Keep my name private in any public acknowledgment</span>
                </label>
            )}
        </div>
    );
}
